
import React, { useState, useEffect } from 'react';
import { imageStyleClasses, overlayClasses } from './HeroUtils';

type HeroBackgroundProps = {
  backgroundImage?: string;
  overlay?: boolean;
  overlayOpacity: 'light' | 'medium' | 'heavy';
  imageStyle: 'darken' | 'natural' | 'brighten' | 'darker' | 'blur';
  imagePosition?: 'top' | 'center' | 'bottom';
  bgColorClass: string;
  descriptiveAlt?: string;
};

const HeroBackground: React.FC<HeroBackgroundProps> = ({
  backgroundImage,
  overlay = false, // Default to false to remove overlays
  overlayOpacity,
  imageStyle,
  imagePosition = 'center',
  bgColorClass,
  descriptiveAlt
}) => {
  const imageStyleClass = imageStyleClasses[imageStyle] || imageStyleClasses.natural;
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  // Determine background position class based on the imagePosition prop
  const bgPositionClass = {
    'top': 'bg-top',
    'center': 'bg-center',
    'bottom': 'bg-bottom'
  }[imagePosition] || 'bg-center';
  
  useEffect(() => {
    if (!backgroundImage) return;
    
    const img = new Image();
    img.src = backgroundImage;
    
    img.onload = () => {
      console.log('Hero image loaded successfully:', backgroundImage);
      setImageLoaded(true);
    };
    
    img.onerror = (e) => {
      console.error('Failed to load hero image:', backgroundImage, e);
      setImageError(true);
    };
    
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [backgroundImage]);
  
  console.log('Hero background render - image position:', imagePosition);

  return (
    <>
      {/* Background Gradient (shown while image loads or if error) */}
      <div 
        className={`absolute inset-0 bg-gradient-to-br from-gray-700 to-gray-900 transition-opacity duration-500 ${imageLoaded && !imageError ? 'opacity-0' : 'opacity-100'}`}
        aria-hidden="true"
      ></div>
      
      {/* Background Image */}
      {backgroundImage && (
        <>
          <div
            className={`absolute inset-0 bg-cover ${bgPositionClass} ${imageStyleClass} transition-all duration-500 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
            style={{ backgroundImage: `url(${backgroundImage})` }}
            aria-hidden="true"
          ></div>
          <img src={backgroundImage} alt={descriptiveAlt} className="sr-only" />
        </>
      )}
      
      {/* Overlay Layer - Only render if overlay is explicitly set to true */}
      {overlay && (
        <div 
          className={`absolute inset-0 ${overlayClasses[overlayOpacity]}`} 
          aria-hidden="true"
        ></div>
      )}
      
      {/* Add a very subtle vignette for better text readability without a dark overlay */}
      <div 
        className="absolute inset-0 vignette pointer-events-none"
        aria-hidden="true"
      ></div>
    </>
  );
};

export default HeroBackground;
